import { useMemo } from 'react';

import type { InfiniteScrollOptions } from '@components/virtual-list';

/**
 * Adapts an RTK Query infinite-query result into a table's `infinite` prop.
 *
 * Memoized, and `loadingMessage` narrowed to a string, because the returned
 * object lands in consumers' `useCallback`/`useMemo` dependencies — an inline
 * node there would be a fresh dependency every render, rebuilding a table's
 * whole column set.
 */
export const useInfiniteScrollOptions = (
  query: {
    fetchNextPage: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
  },
  loadingMessage: string,
): InfiniteScrollOptions => {
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;

  return useMemo(
    () => ({
      hasMore: hasNextPage,
      isLoading: isFetchingNextPage,
      loadingMessage,
      loadMore: () => {
        fetchNextPage();
      },
    }),
    [fetchNextPage, hasNextPage, isFetchingNextPage, loadingMessage],
  );
};
